import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pipApi, employeeApi } from '../../services/api';
import { PageHeader, EmptyState, Spinner, Modal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

const STATUS_STYLES = { active:'bg-yellow-100 text-yellow-700', recovered:'bg-green-100 text-green-700', extended:'bg-orange-100 text-orange-700', exit_recommended:'bg-red-100 text-red-700', closed:'bg-gray-100 text-gray-600' };
const ROOT_CAUSES   = ['skill_gap','role_mismatch','personal_circumstances','under_management','other'];

function CreatePipForm({ onSave, onClose }) {
  const { data: emps } = useQuery({ queryKey: ['employees-all'], queryFn: () => employeeApi.list({ per_page: 200 }).then(r => r.data.data) });
  const [form, setForm] = useState({ employee_id:'', root_cause:'skill_gap', start_date:'', notes:'' });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-4">
      <div><label className="label">Employee *</label>
        <select required className="input" value={form.employee_id} onChange={set('employee_id')}>
          <option value="">Select employee…</option>
          {emps?.map(e => <option key={e.id} value={e.id}>{e.name} — {e.role?.replace('_',' ')}</option>)}
        </select>
      </div>
      <div><label className="label">Root cause</label>
        <select className="input" value={form.root_cause} onChange={set('root_cause')}>
          {ROOT_CAUSES.map(c => <option key={c} value={c}>{c.replace('_',' ')}</option>)}
        </select>
      </div>
      <div><label className="label">Start date *</label><input required type="date" className="input" value={form.start_date} onChange={set('start_date')} /></div>
      <div><label className="label">Initial notes</label><textarea className="input h-20 resize-none" value={form.notes} onChange={set('notes')} placeholder="Context about why this PIP is being initiated…" /></div>
      <div className="flex justify-end gap-3"><button type="button" onClick={onClose} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Create PIP</button></div>
    </form>
  );
}

export default function PipTracker() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const { data, isLoading } = useQuery({ queryKey: ['pips', statusFilter], queryFn: () => pipApi.list({ status: statusFilter||undefined }).then(r => r.data) });
  const createMut = useMutation({ mutationFn: pipApi.create, onSuccess: () => { qc.invalidateQueries(['pips']); setShowForm(false); toast.success('PIP created'); } });
  const pips = data?.data || [];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader title="PIP Tracker" subtitle="Manage performance improvement plans"
        actions={<button onClick={() => setShowForm(true)} className="btn-primary"><PlusIcon className="w-4 h-4"/>Create PIP</button>} />
      <div className="flex gap-2 mb-5">
        {['active','recovered','extended','exit_recommended','closed',''].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} className={clsx('px-3 py-1.5 rounded-full text-xs font-medium border transition-colors', statusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')}>
            {s || 'All'}
          </button>
        ))}
      </div>
      {isLoading ? <div className="flex justify-center py-20"><Spinner /></div>
      : pips.length === 0 ? <EmptyState title="No PIPs found" description="No performance improvement plans match the current filter." />
      : (
        <div className="space-y-3">
          {pips.map(pip => {
            const daysLeft = pip.end_date ? Math.ceil((new Date(pip.end_date) - new Date()) / 86400000) : null;
            return (
              <div key={pip.id} className="card p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-gray-900">{pip.employee?.name}</h3>
                      <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_STYLES[pip.status]||'bg-gray-100 text-gray-600')}>{pip.status?.replace('_',' ')}</span>
                    </div>
                    <p className="text-sm text-gray-500 capitalize">{pip.employee?.role?.replace('_',' ')} · Root cause: {pip.root_cause?.replace('_',' ') || '—'}</p>
                    <p className="text-xs text-gray-400 mt-1">{pip.start_date} → {pip.end_date}{daysLeft != null && pip.status === 'active' ? ` · ${daysLeft > 0 ? daysLeft + ' days left' : 'Overdue'}` : ''}</p>
                    {pip.notes && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{pip.notes}</p>}
                  </div>
                  <Link to={`/pips/${pip.id}`} className="btn-secondary btn-sm shrink-0 ml-4">View details</Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Modal open={showForm} onClose={() => setShowForm(false)} title="Create PIP" size="md">
        <CreatePipForm onSave={d => createMut.mutate(d)} onClose={() => setShowForm(false)} />
      </Modal>
    </div>
  );
}
